import React from 'react'
import { motion } from 'framer-motion'
import { ArrowDown, ShoppingBag, Download } from 'lucide-react'
import { CyberButton } from '../ui/CyberpunkHUD'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '../../contexts/SupabaseAuthContext'
import { supabase } from '../../config/supabase'

interface HeroSectionHTMLProps {
  onExploreClick: () => void
}

export function HeroSectionHTML({ onExploreClick }: HeroSectionHTMLProps) {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const handleDownloadLauncher = async () => {
    if (!isAuthenticated) {
      toast.error('Você precisa estar logado para baixar o Launcher')
      navigate('/login')
      return
    }

    try {
      toast.loading('Preparando download...')
      
      // Tentar buscar URL atualizada do Supabase
      const { data, error } = await supabase
        .from('software_versions')
        .select('download_url')
        .eq('id', 'overpixel-launcher')
        .single()

      let downloadUrl = 'https://www.dropbox.com/scl/fi/g7x6j3q8k9l2m4n5p0q1r/Overpixel-Launcher-Setup.exe?rlkey=8j9k0l1m2n3o4p5q6r7s8t9u0&dl=1'

      if (data?.download_url) {
        downloadUrl = data.download_url
        // Ajuste para Dropbox se necessário
        if (downloadUrl.includes('dropbox.com')) {
           if (downloadUrl.includes('dl=0')) downloadUrl = downloadUrl.replace('dl=0', 'dl=1')
           else if (!downloadUrl.includes('dl=')) downloadUrl += (downloadUrl.includes('?') ? '&dl=1' : '?dl=1')
        }
      } else if (error) {
        console.error('Erro ao buscar URL do launcher:', error)
      }

      toast.dismiss()
      
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', 'Overpixel-Launcher-Setup.exe')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      toast.success('Download iniciado!')
    } catch (err) {
      toast.dismiss()
      toast.error('Erro ao iniciar download. Tente novamente.')
      console.error(err)
    }
  }

  return (
    <div className="w-full max-w-7xl mx-auto flex flex-col items-center justify-center text-center z-10 relative">
      
      {/* Logo */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex justify-center mb-8"
      >
        <img 
          src="/Logo da overpixel sem fundo.png" 
          alt="Overpixel Logo" 
          className="w-48 md:w-64 lg:w-80 object-contain drop-shadow-[0_0_30px_rgba(34,211,238,0.3)]"
          style={{ mixBlendMode: 'screen' }} 
        />
      </motion.div>

      {/* Main Title */}
      <motion.h1 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, duration: 0.8, type: "spring" }}
        className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-tight tracking-tighter"
      >
        Revolucionamos a <br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-white to-purple-500 animate-gradient-x">
          Comunicação Visual Têxtil
        </span>
      </motion.h1>

      {/* Subtitle */}
      <motion.p 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-lg md:text-2xl text-gray-400 mb-10 font-light max-w-2xl mx-auto leading-relaxed"
      >
        Softwares futurísticos para impressão DTF e novas tecnologias. 
        <br/>Potencialize sua produção com inteligência artificial.
      </motion.p>

      {/* CTA */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="flex flex-col items-center gap-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          <CyberButton 
            onClick={handleDownloadLauncher}
            className="group !text-base !px-8 !py-4 shadow-[0_0_30px_rgba(34,211,238,0.3)] hover:shadow-[0_0_50px_rgba(34,211,238,0.5)]"
          >
            <span className="flex items-center gap-2">
              BAIXAR LAUNCHER <Download className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
            </span>
          </CyberButton>
          
          <button 
            onClick={() => navigate('/softwares')}
            className="px-8 py-4 rounded-lg border border-white/10 hover:bg-white/5 text-gray-300 font-mono text-sm uppercase tracking-wider transition-all hover:text-white flex items-center gap-2"
          >
            <ShoppingBag className="w-4 h-4" />
            Ir para Loja
          </button>
        </div>

        {/* Info Text */}
        <p className="text-sm text-cyan-400/80 font-mono tracking-wide bg-cyan-950/30 px-4 py-2 rounded-full border border-cyan-500/20">
          ℹ️ Utilize o Launcher para baixar e gerenciar todos os softwares da Overpixel
        </p>
      </motion.div>
    </div>
  )
}
